import {WriteButton} from '../common/buttons/WriteActions';
import React, {useEffect, useState} from 'react';
import {Alert, Box, FormControl, InputAdornment, InputLabel, MenuItem, Select, Snackbar, Stack, TextField} from '@mui/material';
import {itemAPI} from '../../services/ItemServices';
import {getStyles} from './tableCommon';
import {ItemTable} from "./item/ItemTable";
import {CreateItem} from "./dialogs/CreateItem";
import {ItemDto} from "../../models/Items/IItem";
import {Clear} from "@mui/icons-material";
import useDebounce from "../../hooks/useDebounce";
import IconButton from "@mui/material/IconButton";
import {useAppTranslation} from "@mybake/ui/hooks/useAppTranslation";
import {usePageLimit} from "../../hooks/usePageLimit";
import {AddPurchaseDialog} from './dialogs/AddPurchaseDialog';
import {EditIngredientDialog} from './dialogs/EditIngredientDialog';
import SwapVertIcon from '@mui/icons-material/SwapVert';
import {getApiErrorKind, getApiErrorMessage, getApiTraceId} from '../../common/apiErrors';
import TraceIdFeedback from '../feedback/TraceIdFeedback';
import {PageHeader} from '../common/layout/PageHeader';
import {Surface} from '../common/layout/Surface';
import {ContentSkeleton} from '../common/loading/ContentSkeleton';

function Items() {
    const {t} = useAppTranslation();
    const {boxItemsLoading} = getStyles();
    const [page, setPage] = useState(0);
    const [limit, setLimit] = usePageLimit('items');
    const [openCreateItemDialog, setOpenCreateItemDialog] = useState<boolean>(false);
    const [openPurchaseDialog, setOpenPurchaseDialog] = useState<boolean>(false);
    const [openEditIngredientDialog, setOpenEditIngredientDialog] = useState<boolean>(false);
    const [searchPhrase, setSearchPhrase] = useState('');
    const [selectedItem, setSelectedItem] = useState<ItemDto | undefined>(undefined);
    const [expandedItemId, setExpandedItemId] = useState<string | null>(null);
    const [sortField, setSortField] = useState<'name' | 'count' | 'price' | 'weight'>('name');
    const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');
    const descending = sortDirection === 'desc';
    const debouncedSearchPhrase = useDebounce(searchPhrase, 600);
    const [serverToast, setServerToast] = useState('');
    const [traceId, setTraceId] = useState('');

    const {
        data: items,
        error: getAllItemsError,
        isLoading: getAllItemsIsLoading,
        isSuccess: getAllItemsIsSuccess,
    } = itemAPI.useGetItemsByPagesQuery({
        page: page,
        limit: limit,
        searchPhrase: debouncedSearchPhrase.trim().length >= 3
            ? debouncedSearchPhrase.trim()
            : "",
        sortBy: sortField,
        descending,
    });

    const handleSort = (field: typeof sortField) => {
        setSortDirection(prevDir => {
            const isSameField = field === sortField;

            return isSameField
                ? (prevDir === 'asc' ? 'desc' : 'asc')
                : 'asc';
        });

        setSortField(field);
    };

    const handelCreateItemDialog = () => {
        setOpenCreateItemDialog(!openCreateItemDialog);
    };

    const openPurchase = (item: ItemDto) => {
        setSelectedItem(item);
        setOpenPurchaseDialog(true);
    };
    const openEdit = (item: ItemDto) => {
        setSelectedItem(item);
        setOpenEditIngredientDialog(true);
    };

    const handlePageChange = (newPage: number) => setPage(newPage);
    const handlePageSizeChange = (newSize: number) => {
        setLimit(newSize);
        setPage(0);
    }

    useEffect(() => {
        setPage(0);
    }, [debouncedSearchPhrase]);

    useEffect(() => {
        if (limit < 1 || limit > 200) setLimit(15);
    }, [limit, setLimit]);

    useEffect(() => {
        if (!getAllItemsError || getApiErrorKind(getAllItemsError) !== 'server') return;
        setServerToast(getApiErrorMessage(getAllItemsError, t));
        setTraceId(getApiTraceId(getAllItemsError));
    }, [getAllItemsError, t]);

    return (
        <Box sx={{width: '100%'}}>
            <PageHeader title={t('navigation:nav_items')} action={<WriteButton variant="contained" onClick={handelCreateItemDialog}>{t('items:create_item')}</WriteButton>}/>
            <Stack direction="row" spacing={1} alignItems="center" sx={{mb: 2}}><FormControl size="small" sx={{minWidth: 140, flex: {xs: 1, sm: 'initial'}}}><InputLabel>{t('items:sort_by')}</InputLabel><Select value={sortField} label={t('items:sort_by')} onChange={event => handleSort(event.target.value as typeof sortField)}><MenuItem value="name">{t('items:sort_name')}</MenuItem><MenuItem value="count">{t('items:sort_count')}</MenuItem><MenuItem value="price">{t('items:sort_price')}</MenuItem><MenuItem value="weight">{t('items:sort_weight')}</MenuItem></Select></FormControl><IconButton aria-label={sortDirection === 'asc' ? t('items:ascending') : t('items:descending')} onClick={() => setSortDirection(previous => previous === 'asc' ? 'desc' : 'asc')}><SwapVertIcon/></IconButton></Stack>

            <Box>
                {getAllItemsIsLoading && (
                    <Box sx={boxItemsLoading}><ContentSkeleton variant="list"/></Box>
                )}

                {getAllItemsIsSuccess && !getAllItemsIsLoading && !getAllItemsError && (
                    <Surface sx={{width: '100%'}}>
                        <Box sx={{p: 2}}>
                            <TextField
                                label={t('common:search')}
                                variant="outlined"
                                focused
                                size="small"
                                fullWidth
                                value={searchPhrase}
                                onChange={(e) => {
                                    setSearchPhrase(e.target.value);
                                }}
                                InputProps={{
                                    endAdornment: searchPhrase ? (
                                        <InputAdornment position="end">
                                            <IconButton
                                                size="small"
                                                onClick={() => setSearchPhrase("")}
                                            >
                                                <Clear fontSize="small"/>
                                            </IconButton>
                                        </InputAdornment>
                                    ) : null,
                                }}
                            />
                        </Box>
                        <ItemTable data={items.data}
                                   totalCount={items.totalCount}
                                   pageSize={limit}
                                   expandedItemId={expandedItemId}
                                   onToggleItem={(id) => setExpandedItemId(previous => previous === id ? null : id)}
                                   currentPage={page}
                                   onPageChange={handlePageChange}
                                   onPageSizeChange={handlePageSizeChange}
                                   onAddPurchase={openPurchase}
                                   onEdit={openEdit}/>
                    </Surface>)}
            </Box>

            <CreateItem handleDialog={handelCreateItemDialog} open={openCreateItemDialog}/>
            {selectedItem && <AddPurchaseDialog item={selectedItem} open={openPurchaseDialog}
                                                onClose={() => setOpenPurchaseDialog(false)}/>}
            <EditIngredientDialog item={selectedItem} open={openEditIngredientDialog}
                                  onClose={() => setOpenEditIngredientDialog(false)}/>

            {!getAllItemsIsLoading && getAllItemsError && getApiErrorKind(getAllItemsError) !== 'server' && <Alert severity="error">{getApiErrorMessage(getAllItemsError, t)}</Alert>}
            <Snackbar open={!!serverToast} autoHideDuration={6000} onClose={() => setServerToast('')}>
                <Alert severity="error" onClose={() => setServerToast('')}>{serverToast}<TraceIdFeedback traceId={traceId}/></Alert>
            </Snackbar>
        </Box>
    );
}

export default Items;
